import { useState, useCallback } from 'react'
import { cva } from 'class-variance-authority'
import { useTemplate } from '@/contexts/TemplateContext'
import { cn } from '@/lib/utils'
import type { GalleryImage, GalleryProps } from './types'
import { GalleryLightbox } from './GalleryLightbox'

const galleryVariants = cva('grid', {
  variants: {
    layout: {
      grid: '',
      masonry: '',
      carousel: 'flex overflow-x-auto gap-4 snap-x snap-mandatory',
      featured: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4',
    },
    columns: {
      '2': 'grid-cols-2',
      '3': 'grid-cols-2 md:grid-cols-3',
      '4': 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
      auto: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5',
    },
    gap: {
      1: 'gap-1',
      2: 'gap-2',
      3: 'gap-3',
      4: 'gap-4',
      6: 'gap-6',
    },
  },
  defaultVariants: {
    layout: 'grid',
    columns: 'auto',
    gap: 3,
  },
})

const aspectRatioClasses = {
  square: 'aspect-square',
  video: 'aspect-video',
  portrait: 'aspect-[3/4]',
  auto: '',
}

export function Gallery({
  images,
  layout = 'grid',
  limit,
  enableLightbox = true,
  showCaptions = false,
  showFeaturedBadge = false,
  aspectRatio = 'square',
  gapSize = 3,
  className,
  onImageClick,
}: GalleryProps) {
  const { colors } = useTemplate()
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  const displayImages = limit ? images.slice(0, limit) : images

  const handleImageClick = useCallback(
    (image: GalleryImage, index: number) => {
      if (enableLightbox) {
        setLightboxIndex(index)
      }
      onImageClick?.(image, index)
    },
    [enableLightbox, onImageClick]
  )

  const closeLightbox = useCallback(() => {
    setLightboxIndex(null)
  }, [])

  if (displayImages.length === 0) {
    return null
  }

  const renderImage = (image: GalleryImage, index: number) => {
    const isFeatured = image.is_featured && showFeaturedBadge
    const aspectClass = aspectRatio !== 'auto' ? aspectRatioClasses[aspectRatio] : ''

    return (
      <div
        key={image.id}
        className={cn(
          'relative group overflow-hidden rounded-lg cursor-pointer',
          layout === 'featured' && index === 0 && 'md:col-span-2 md:row-span-2',
          aspectClass
        )}
        onClick={() => handleImageClick(image, index)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleImageClick(image, index)
          }
        }}
        aria-label={image.alt_text || image.caption || `Gallery image ${index + 1}`}
      >
        <img
          src={image.thumbnail_url || image.url}
          alt={image.alt_text || image.caption || 'Gallery image'}
          className={cn(
            'w-full h-full object-cover transition-transform duration-300 group-hover:scale-105',
            aspectClass && 'hover:scale-105'
          )}
          loading="lazy"
        />

        {/* Featured badge */}
        {isFeatured && (
          <div
            className="absolute top-2 left-2 px-2 py-1 text-xs font-bold rounded-md"
            style={{
              backgroundColor: colors.colors.accent,
              color: colors.colors.primary,
            }}
          >
            Featured
          </div>
        )}

        {/* Overlay with caption on hover */}
        {showCaptions && image.caption && (
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
            <p className="text-white text-sm truncate">{image.caption}</p>
          </div>
        )}

        {/* Lightbox trigger indicator */}
        {enableLightbox && (
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/20">
            <div
              className="p-2 rounded-full"
              style={{ backgroundColor: colors.colors.primary, color: colors.colors.secondary }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                />
              </svg>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      <div
        className={cn(
          galleryVariants({ layout, columns: layout === 'grid' ? 'auto' : undefined, gap: gapSize }),
          layout === 'grid' && 'grid',
          className
        )}
      >
        {layout === 'carousel'
          ? displayImages.map((image, index) => (
              <div key={image.id} className="flex-shrink-0 snap-center">
                {renderImage(image, index)}
              </div>
            ))
          : displayImages.map((image, index) => renderImage(image, index))}
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <GalleryLightbox
          images={displayImages}
          initialIndex={lightboxIndex}
          onClose={closeLightbox}
        />
      )}
    </>
  )
}
